/*!
* Define ducker properties and behaviour.
*/
async function getDuckerWorklet(ctx, options) {
	const path = window.location.pathname.split('/');
	const workletDir = path.slice(0, path.length - 1).join('/');
	await ctx.audioWorklet.addModule(`${workletDir}/ducker2Worklet.js`);
	return new AudioWorkletNode(ctx, 'ducker-processor', options);
}

export default class Ducker {
	/* DEFAULT LOGIC (METHODS) THAT SHOULD BE IMPLEMENTED IN EACH PLUGIN */

	//Used for getting current values for save
	getValues() {
		let values = {};

		values.dwSettings = { ...this.dwSettings };
		values.masterGainSettings = { ...this.masterGainSettings };

		return values;
	}

	// Used for setting stored values.
	// For each value, we check to see if it was passed in the 
	// values parameter before setting it to a default value
	// We'll do the same thing with a ternary for all the properties.
	setValues(values) {
		if (typeof values == 'undefined' || Object.keys(values).length === 0) { // if null or empty object passed

			// Set all settings to defaults

			// The default compressor settings for each frequency band
			this['dwSettings'] = {
				threshold: this.threshold,
				reduction: this.reduction,
				attack: this.attack,
				release: this.release,
				hold: this.hold,
				latencyMode: this.latencyMode
			};

			this['attenuation'] = 0;

			// Master gain
			this.masterGainSettings = {
				gain: this.masterGainSettings.gain,
				inputGain: this.masterGainSettings.inputGain
			};
		} else {
			if (this.isDebug) {
				console.debug('Set ducker values', { values, initialized: this.initialized });
			}

			// Check for the dwSettings property. If it's there use it, otherwise set an empty object.
			this['dwSettings'] = values.hasOwnProperty('dwSettings') ? values['dwSettings'] : {};
			// Set the compressor settings
			let passedDwSettings = values['dwSettings']; // Put them in a variable for readability of the ternaries
			this['dwSettings'] = {
				threshold: this.isEmpty(passedDwSettings, 'threshold') ? this.threshold : passedDwSettings['threshold'],
				reduction: this.isEmpty(passedDwSettings, 'reduction') ? this.reduction : Math.min(0, passedDwSettings['reduction']),
				attack: this.isEmpty(passedDwSettings, 'attack') ? this.attack : passedDwSettings['attack'],
				release: this.isEmpty(passedDwSettings, 'release') ? this.release : passedDwSettings['release'],
				hold: this.isEmpty(passedDwSettings, 'hold') ? this.hold : passedDwSettings['hold'],
				latencyMode: this.isEmpty(passedDwSettings, 'latencyMode') ? this.latencyMode : passedDwSettings['latencyMode'],
			};

			//COMMENT: setValues should not only set defaults to variables, but also set values to audio nodes, if initialized
			this.updateThreshold(this['dwSettings']['threshold']);
			this.updateReduction(this['dwSettings']['reduction']);
			this.updateAttack(this['dwSettings']['attack']);
			this.updateRelease(this['dwSettings']['release']);
			this.updateHold(this['dwSettings']['hold']);
			this.updateLatencyMode(this['dwSettings']['latencyMode']);

			this['attenuation'] = 0;

			// Check for the master gain property. If it's there use it, otherwise set an empty object.
			this['masterGainSettings'] = values.hasOwnProperty('masterGainSettings') ? values['masterGainSettings'] : {};

			// Master gain
			let passedMasterGainSettings = values.masterGainSettings;
			this.masterGainSettings.gain = this.isEmpty(passedMasterGainSettings, 'gain') ? this.masterGainSettings.gain : passedMasterGainSettings['gain'];
			this.masterGainSettings.inputGain = this.isEmpty(passedMasterGainSettings, 'inputGain') ? this.masterGainSettings.inputGain : passedMasterGainSettings['inputGain'];

			//COMMENT: setValues should not only set defaults to variables, but also set values to audio nodes, if initialized
			this.updateMasterGain(this.masterGainSettings.gain);
			this.updateInputGain(this.masterGainSettings.inputGain);
		}
	}

	//Rows: array of rows (visually each row will be stacked vertically), inside array of elements - they will be stacked horizontally in a row - affectors (value changers) or indicators
	//Availale kinds: 'indicator', 'affector'
	//Available types for affectors: 'round-button' (min, max, step), 'slider' (min, max, step),
	//Available types for indicators: 'vu-meter' (min, max, orientation(vertical, horizontal))
	//Indicator can show values in pure value (like db) or percent, but better in pure value. Examples: { value: -15 }, { percent: 50 }
	getUI() {
		let ui = {
			rows: [],
			width: 880,
			height: 340
		};

		let colors = ['#4dffae', '#73b1e3', '#c495fd']
		let propertyRows = [
			[
				{
					kind: 'affector', type: 'round-button', width: 75, color: colors[0], title: 'Threshold', min: this.minThreshold, max: this.maxThreshold, step: .1, round: 1, unit: 'dB',
					setValue: (value) => { return this.updateThreshold(value) },
					getValue: () => { return this.dwSettings.threshold }
				},
				{
					kind: 'affector', type: 'round-button', width: 75, color: colors[0], title: 'Reduction', min: this.minReduction, max: this.maxReduction, step: .1, round: 1, unit: 'dB',
					setValue: (value) => { return this.updateReduction(value) },
					getValue: () => { return this.dwSettings.reduction }
				},
				{
					kind: 'affector', type: 'round-button', width: 75, color: colors[0], title: 'Attack', min: this.minAttack, max: this.maxAttack, step: 1, unit: 'ms',
					setValue: (value) => { return this.updateAttack(value) },
					getValue: () => { return this.dwSettings.attack }
				},
			],
			[
				{
					kind: 'affector', type: 'round-button', width: 75, color: colors[0], title: 'Release', min: this.minRelease, max: this.maxRelease, step: 1, unit: 'ms',
					setValue: (value) => { return this.updateRelease(value) },
					getValue: () => { return this.dwSettings.release }
				},
				{
					kind: 'affector', type: 'round-button', width: 75, color: colors[1], title: 'Hold', min: 0, max: 10000, step: 1, stepSensivity: 0.025, unit: 'ms',
					setValue: (value) => { return this.updateHold(value) },
					getValue: () => { return this.dwSettings.hold }
				},
				{
					kind: 'affector', type: 'switch', width: 50, title: 'Latency mode', min: 0, max: 1, step: 1, round: 1,
					setValue: (value) => { return this.updateLatencyMode(value) },
					getValue: () => { return this.dwSettings.latencyMode }
				},
			]
		];

		let masterGainRows = [
			{
				kind: 'affector', type: 'round-button', width: 75, title: 'Input gain', min: -60, max: 6, step: .1, round: 1, unit: 'dB',
				setValue: (value) => { return this.updateInputGain(value) },
				getValue: () => { return this.masterGainSettings.inputGain }
			},
			{
				kind: 'affector', type: 'round-button', width: 75, title: 'Master Gain', min: -60, max: 6, step: .1, round: 1, unit: 'dB',
				setValue: (value) => { return this.updateMasterGain(value) },
				getValue: () => { return this.masterGainSettings.gain }
			}
		];

		ui.rows.push([
			{
				kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'Sidechain Level', min: -60, max: 3, getValue: () => {
					return {
						value: this.sidechainLevel
					}
				}
			},
			{
				rows: [
					...propertyRows
				],
				orientation: 'horizontal'
			},
			{
				rows: [
					masterGainRows
				],
				orientation: 'vertical'
			},
			{
				kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'Attenuation', min: 0, max: 60, redPoint: 54, yellowPoint: 45, getValue: () => {
					// this.attenuation / 2 to match attenuation of the plugin
					return { 
						value: this.attenuation / 2
					}
				}
			},
			{
				kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'In', min: -60, max: 3, getValue: () => {
					return {
						value: this.preMetering
					}
				}
			},
			{
				kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'Out', min: -60, max: 3, getValue: () => {
					return {
						value: this.postMetering 
					}
				}
			}
		]);
		return ui;
	}


	/**
	 * Create nodes and connect them
	 * 
	 * @returns object containing input and output node
	 */
	async getAudioPipeline(opts) {
		this.ctx = opts?.audioContext || new AudioContext();

		// Create pre analyser node
		this.preAnalyserNode = this.ctx.createAnalyser(); // The input audio analyser

		// Input gain node
		this.inputGainNode = this.ctx.createGain();

		// Output gain node
		this.outputGainNode = this.ctx.createGain();

		// Master gain node
		this.masterGainNode = this.ctx.createGain();

		// Create post analyser node
		this.postAnalyserNode = this.ctx.createAnalyser(); // The output audio analyser

		// Create worklet node
		this.duckerWorkletNode = await getDuckerWorklet(this.ctx, { numberOfInputs: 2, numberOfOutputs: 1, outputChannelCount: [2] });

		// Create attenuation analyser
		this.attenuationAnalyserNode = this.ctx.createAnalyser();

		// Create channel merger node
		this.channelMergerNode = this.ctx.createChannelMerger(3);

		// Create channel spliite node
		this.splitter = this.ctx.createChannelSplitter(2);

		// Connect nodes
		this.inputGainNode.connect(this.preAnalyserNode);
		this.preAnalyserNode.connect(this.duckerWorkletNode, 0, 1);
		// I don't use outputGainNode, a masterGainNode is used instead
		//this.duckerWorkletNode.connect(this.outputGainNode, 1, 0);
		this.duckerWorkletNode.connect(this.attenuationAnalyserNode, 0, 0);
		this.attenuationAnalyserNode.connect(this.masterGainNode);
		this.masterGainNode.connect(this.postAnalyserNode);

		this.inputNode = this.inputGainNode;
		this.outputNode = this.postAnalyserNode;

		this.init();

		// To input will be connected source (with other effects), output will be connected to destination (or next effect)
		return {
			input: this.inputGainNode,
			output: this.postAnalyserNode
		}
	}

	initSidechain(inputs) {
		(inputs || []).map(input => {
			if (input.connect) {
				input.connect(this.duckerWorkletNode, 0, 0);
			}
		});
	}

	// Called on playing state change (1 - playing, 0 - stop)
	setPlayState(value) {
		this.initialized = !!value;
		this.playing = !!value;

		if (this.isDebug) {
			console.debug('Set play state changed', { value });
		}

		if (value) {
			this.updateMeters();
		} else {
			this.attenuation = 0;
			this.preMetering = 0;
			this.postMetering = 0;
			this.sidechainLevel = 0;
		}
	}

	constructor(opts) {
		this.ctx = opts.context;
		this.player = opts.player;

		// Show console messages
		this.isDebug = opts.isDebug;

		if (this.isDebug) {
			console.log('Creating ducker... ');
		}

		// Default ducker worklet settings
		this.threshold = -40;
		this.reduction = 0;
		this.attack = 100; // ms
		this.release = 100; // ms
		this.hold = 0;
		this.latencyMode = false;

		// Default gain
		this.gain = 1;
		this.inputGain = 1;
		this.outputGain = 1;

		// Default max input values
		this.maxThreshold = 0; // Max value of threshold range sliders
		this.maxReduction = 0;
		this.maxAttack = 1000; // In ms
		this.maxRelease = 2000; // In ms
		this.maxGain = 5;

		// Default min inupt values
		this.minThreshold = -100; // Min value of threshold range sliders
		this.minReduction = -60;
		this.minAttack = 1; // In seconds
		this.minRelease = 1; // In seconds
		this.minGain = 0;

		// Master gain defaults in Db
		this.masterGainSettings = {
			gain: 0,
			inputGain: 0,
			outputGain: 0
		};

		// Analyser properties
		this.fttSize = 2048; // bytes to allocate for data
		this.preMetering = null;
		this.postMetering = null;
		this.attenuationMetering = null;

		this.initialized = false;

		this.preInit();
	}

	/* PLUGIN LOGIC */

	// Add some basic functionality for before the user is playing an audio file
	preInit() {
		// Test setting values
		// this.setValues(JSON.parse(values));
		this.setValues();
	}

	// This is the last second logic that needs to execute for the plugin to work
	init() {
		this.initNodes(); // Use the values on compressor object to set values of the nodes

		// I removed analyzer init functions, because the analyzers are already initialized in the initNodes function
		//this.initPreAnalyser();
		//this.initPostAnalyser();
		//this.initAttenuationAnalyzer();
		// console.log(JSON.stringify(this.getValues()));

		this.initialized = true;

		this.duckerWorkletNode.port.postMessage({ 'debug': __DEBUG__ });

		// Communication from the worklet file
		this.duckerWorkletNode.port.onmessage = (e) => {
			if ('sidechainLevel' in e.data)
				this.sidechainLevel = e.data.sidechainLevel;
		}
	}

	// Initialize values in nodes
	initNodes() {
		// Initialize attenuation analyser settings
		this.attenuationAnalyserNode.fftSize = this.fttSize;
		this.attenuationFloatTimeData = new Float32Array(
			this.attenuationAnalyserNode.frequencyBinCount
		);
		this.attenuationAnalyserNode.getFloatTimeDomainData(
			this.attenuationFloatTimeData
		);

		// Initialize pre analyser node
		this.preAnalyserNode.fftSize = this.fttSize;
		this.preFloatTimeData = new Float32Array(
			this.preAnalyserNode.frequencyBinCount
		);
		this.preAnalyserNode.getFloatTimeDomainData(
			this.preFloatTimeData
		);

		// Initialize post analyser node
		this.postAnalyserNode.fftSize = this.fttSize;
		this.postFloatTimeData = new Float32Array(
			this.postAnalyserNode.frequencyBinCount
		);
		this.postAnalyserNode.getFloatTimeDomainData(
			this.postFloatTimeData
		);

		// Initialize master gain node value
		this.masterGainNode.gain.setValueAtTime(
			this.masterGainSettings.gain,
			this.ctx.currentTime
		);
	}

	initPreAnalyser() {
		// Analyzer for metering
		this.preAnalyserNode.fftSize = this.fttSize;

		this.preFloatTimeData = new Float32Array(this.preAnalyserNode.frequencyBinCount);
		this.preAnalyserNode.getFloatTimeDomainData(this.preFloatTimeData);
	}

	initPostAnalyser() {
		// Analyzer for metering
		this.postAnalyserNode.fftSize = this.fttSize;

		this.postFloatTimeData = new Float32Array(this.postAnalyserNode.frequencyBinCount);
		this.postAnalyserNode.getFloatTimeDomainData(this.postFloatTimeData);
	}
	
	initAttenuationAnalyser() {
		// Analyzer for metering
		this.attenuationAnalyserNode.fftSize = this.fttSize;

		this.attenuationFloatTimeData = new Float32Array(this.attenuationAnalyserNode.frequencyBinCount);
		this.attenuationAnalyserNode.getFloatTimeDomainData(this.attenuationFoatTimeData);
	}

	updateThreshold(value) {
		this.dwSettings.threshold = value;

		if (__DEBUG__)
			console.debug('Update threshold', { value, b: this, init: this.initialized });

		if (!this.initialized)
			return;

		// Update the Ducker worklet threshold value
		this.duckerWorkletNode.port.postMessage({ 'threshold': value });
	}

	updateHold(value) {
		this.dwSettings.hold = value;

		if (__DEBUG__)
			console.debug('Update hold', { value, b: this, init: this.initialized });

		if (!this.initialized)
			return;

		// Update the Ducker worklet threshold value
		this.duckerWorkletNode.port.postMessage({ 'hold': value });
	}

	updateLatencyMode(value) {
		this.dwSettings.latencyMode = value;

		if (__DEBUG__)
			console.debug('Update latency mode', { value, b: this, init: this.initialized });

		if (!this.initialized)
			return;

		// Update the Ducker worklet value
		this.duckerWorkletNode.port.postMessage({ 'latencyMode': value });
	}

	updateReduction(value) {
		this.dwSettings.reduction = Math.min(0, value);

		if (!this.initialized)
			return;

		// Update the Ducker worklet reduction value
		this.duckerWorkletNode.port.postMessage({ 'reduction': this.dwSettings.reduction });
	}

	updateAttack(value) {
		this.dwSettings.attack = value;

		if (!this.initialized)
			return;

		// Update the Ducker worklet attack value
		this.duckerWorkletNode.port.postMessage({ 'attack': value });
	}

	updateRelease(value) {
		this.dwSettings.release = value;

		if (!this.initialized)
			return;

		// Update the Ducker worklet release value
		this.duckerWorkletNode.port.postMessage({ 'release': value });
	}

	updateMasterGain(value) {
		this.masterGainSettings.gain = value;

		if (!this.initialized)
			return;

		// Update the node value
		this.masterGainNode.gain.setValueAtTime(this.convertDecibelsToGain(value), this.ctx.currentTime);
	}

	updateInputGain(value) {
		this.masterGainSettings.inputGain = value;

		if (!this.initialized)
			return;

		// Update the node value
		this.inputGainNode.gain.setValueAtTime(this.convertDecibelsToGain(value), this.ctx.currentTime);
	}

	updateOutputGain(value) {
		if (!this.initialized)
			return;

		// Update the node value
		this.outputGainNode.gain.setValueAtTime(this.convertDecibelsToGain(value), this.ctx.currentTime);
	}

	// Check if the object contains the property and
	// and the property is not null or empty
	isEmpty(object, property) {
		if (object.hasOwnProperty(property))
			return false;
		return true;
	}

	// The meters are acting as peak meters
	updateMeters() {
		// Schedule the next update
		if (this.playing) {
			requestAnimationFrame(() => this.updateMeters()); // Recursive
		}

		this.updatePreMeters();
		this.updateAttenuationMeters();
		this.updatePostMeters();
	}

	updateAttenuationMeters() {
		// Get the new frequency data
		this.attenuationAnalyserNode.getFloatTimeDomainData(this.attenuationFloatTimeData);

		let sampleBuffer = this.attenuationFloatTimeData;
		// Compute peak instantaneous power over the interval.
		let peakInstantaneousPower = 0;
		for (let i = 0; i < sampleBuffer.length; i++) {
			const power = sampleBuffer[i] ** 2;
			peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
		}
		const peakInstantaneousPowerDecibels = this.convertGainToDecibels(peakInstantaneousPower);

		this.attenuationMetering = peakInstantaneousPowerDecibels;

		// Get the reduction value in dB by subtracting input level from ouput level
		let totalReduction = 0;

		if (this.postMetering < this.attenuationMetering) {
			totalReduction = Math.abs(this.preMetering - this.attenuationMetering);
		}
		// Set attenuation meter
		this.attenuation = totalReduction;
	}

	updatePreMeters() {
		// Get the new frequency data
		this.preAnalyserNode.getFloatTimeDomainData(this.preFloatTimeData);

		let sampleBuffer = this.preFloatTimeData;
		// Compute peak instantaneous power over the interval.
		let peakInstantaneousPower = 0;
		for (let i = 0; i < sampleBuffer.length; i++) {
			const power = sampleBuffer[i] ** 2;
			peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
		}
		const peakInstantaneousPowerDecibels = this.convertGainToDecibels(peakInstantaneousPower);

		this.preMetering = peakInstantaneousPowerDecibels;
	}

	updatePostMeters() {
		// Get the new frequency data
		this.postAnalyserNode.getFloatTimeDomainData(this.postFloatTimeData);

		let sampleBuffer = this.postFloatTimeData;
		// Compute peak instantaneous power over the interval.
		let peakInstantaneousPower = 0;
		for (let i = 0; i < sampleBuffer.length; i++) {
			const power = sampleBuffer[i] ** 2;
			peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
		}
		const peakInstantaneousPowerDecibels = this.convertGainToDecibels(peakInstantaneousPower);

		this.postMetering = peakInstantaneousPowerDecibels;
	}

	convertDecibelsToGain(db) {
		return Math.pow(10, (db / 20));
	}

	convertGainToDecibels(gain) {
		return 20 * Math.log10(gain);
	}
}